<template>
  <div class="auth-page">
    <script2 v-if="clientID" src="https://accounts.google.com/gsi/client?hl=en-US" />

    <div class="auth-form">
      <div class="auth-form__wrapper">
        <a href="/" class="auth-page__a-logo">
          <img
            class="auth-page__logo"
            src="@/assets/media/logo.png"
            srcset="@/assets/media/logo@2x.png 2x"
          />
        </a>

        <template v-if="showLogin">
          <form id="login" data-test-id="form-login" @submit.prevent="handleLogin">
            <div class="auth-form__header">
              <i class="material-icons">exit_to_app</i>
              <span>Log In</span>
              <router-link :to="{ name: 'signup' }" style="float: right">
                Create an account
              </router-link>
            </div>

            <div class="field-wrapper">
              <i>alternate_email</i>
              <!--type="email" is removed to allow usage of testing 'admin' account-->
              <input
                ref="email"
                v-model="loginEmail"
                v-focus
                autocomplete="email"
                name="email"
                placeholder="email"
                type="email"
                data-test-id="input-email"
              />
            </div>

            <div class="field-wrapper">
              <i>vpn_key</i>
              <input
                v-model="loginPassword"
                autocomplete="current-password"
                class="auth-form__password-input"
                name="password"
                placeholder="password"
                :type="isLoginPasswordVisible ? 'text' : 'password'"
                data-test-id="input-password"
              />
              <button
                type="button"
                class="auth-form__password-toggle"
                :aria-label="isLoginPasswordVisible ? 'Hide password' : 'Show password'"
                :title="isLoginPasswordVisible ? 'Hide password' : 'Show password'"
                data-test-id="btn-toggle-login-password"
                @click="isLoginPasswordVisible = !isLoginPasswordVisible"
              >
                <span class="material-icons">
                  {{ isLoginPasswordVisible ? 'visibility_off' : 'visibility' }}
                </span>
              </button>
            </div>

            <button
              type="submit"
              :class="{ 'auth-form__btn-submit--loading': isLoading }"
              class="auth-form__btn-submit"
              :disabled="isLoading"
              data-test-id="btn-login"
            >
              <img
                v-show="isLoading"
                src="@/assets/img/loader-dual-ring.svg"
                class="auth-form__loader"
              />
              <span v-show="!isLoading"> Log in </span>
            </button>

            <div v-if="error" class="auth__error" data-test-id="message-error">{{ error }}</div>

            <p class="recover-password-label">
              <router-link class="text-gray" :to="{ name: 'recover-password' }">
                recover password
              </router-link>
              , or
            </p>
          </form>

          <div v-if="clientID">
            <div
              id="g_id_onload"
              :data-client_id="clientID"
              data-context="signin"
              data-ux_mode="popup"
              data-callback="handleCredentialResponse"
              data-auto_prompt="false"
            ></div>

            <div
              class="g_id_signin"
              data-locale="en-US"
              data-type="standard"
              data-shape="rectangular"
              data-theme="outline"
              data-text="signin_with"
              data-size="large"
              data-logo_alignment="center"
              data-width="276"
            ></div>
          </div>
        </template>

        <template v-else>
          <form id="signup" method="post" data-test-id="form-registration" @submit.prevent="signup">
            <div class="auth-form__header">
              <i class="material-icons">person_add</i>
              <span>Sign Up</span>
              <router-link :to="{ name: 'login' }" style="float: right">Log In</router-link>
            </div>

            <div class="field-wrapper">
              <i>alternate_email</i>
              <input
                ref="email"
                v-model="signupEmail"
                v-focus
                :class="{ 'input--not-valid': !emailValid }"
                autocomplete="email"
                name="new-email"
                placeholder="email"
                type="email"
                data-test-id="input-email"
              />
            </div>

            <div class="field-wrapper">
              <i>vpn_key</i>
              <input
                v-model="signupPassword"
                :class="{ 'input--not-valid': !passwordValid }"
                autocomplete="new-password"
                class="auth-form__password-input"
                name="new-password"
                placeholder="password, 8 symbols minimum"
                :type="isSignupPasswordVisible ? 'text' : 'password'"
                data-test-id="input-password"
              />
              <button
                type="button"
                class="auth-form__password-toggle"
                :aria-label="isSignupPasswordVisible ? 'Hide password' : 'Show password'"
                :title="isSignupPasswordVisible ? 'Hide password' : 'Show password'"
                data-test-id="btn-toggle-signup-password"
                @click="isSignupPasswordVisible = !isSignupPasswordVisible"
              >
                <span class="material-icons">
                  {{ isSignupPasswordVisible ? 'visibility_off' : 'visibility' }}
                </span>
              </button>
            </div>

            <div class="terms">
              By clicking Sign Up, you agree to our
              <a href="/terms/" target="_blank">
                Terms of&nbsp;service, Cookie Policy, Data Policy.
              </a>
            </div>

            <button
              :disabled="!emailValid || !passwordValid || isLoading"
              :class="{ 'auth-form__btn-submit--loading': isLoading }"
              class="auth-form__btn-submit"
              type="submit"
              data-test-id="btn-registration"
            >
              <img
                v-show="isLoading"
                src="@/assets/img/loader-dual-ring.svg"
                class="auth-form__loader"
              />
              <span v-show="!isLoading"> Sign Up </span>
            </button>
          </form>

          <p class="text-gray" style="margin-top: 10px; margin-bottom: 10px">-</p>

          <div v-if="clientID">
            <div
              id="g_id_onload"
              :data-client_id="clientID"
              data-context="signin"
              data-ux_mode="popup"
              data-callback="handleCredentialResponse"
              data-auto_prompt="false"
            ></div>

            <div
              class="g_id_signin"
              data-locale="en-US"
              data-type="standard"
              data-shape="rectangular"
              data-theme="outline"
              data-text="signin_with"
              data-size="large"
              data-logo_alignment="center"
              data-width="276"
            ></div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
  import 'assets/css/style.scss';

  import AuthAPI from '@/api/auth';
  import { AuthService } from '@/services/auth.service';

  let emailRegex = null;

  function validateEmail(email) {
    if (emailRegex === null) {
      emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    }
    return emailRegex.test(String(email).toLowerCase());
  }

  function validatePassword(pass) {
    return pass.length >= 8;
  }

  export default {
    name: 'AuthPage',

    data: () => ({
      loginEmail: '',
      loginPassword: '',
      isLoginPasswordVisible: false,

      signupEmail: '',
      signupPassword: '',
      isSignupPasswordVisible: false,

      validate: false,
      error: null,

      isLoading: false,

      clientID: null,
    }),

    computed: {
      showLogin() {
        return this.$route.name === 'login';
      },
      emailValid() {
        if (this.validate) {
          return validateEmail(this.signupEmail);
        }
        return true;
      },
      passwordValid() {
        if (this.validate) {
          return validatePassword(this.signupPassword);
        }
        return true;
      },
    },

    mounted() {
      this.$refs.email.focus();
      this.initGoogleAuth();
    },

    beforeRouteLeave(to, from, next) {
      this.error = null;
      this.validate = false;
      this.isLoginPasswordVisible = false;
      this.isSignupPasswordVisible = false;
      next();
      this.$refs.email.focus();
    },

    methods: {
      initGoogleAuth() {
        window.handleCredentialResponse = (response) => {
          this.signInWithGoogle(response.credential);
        };

        AuthAPI.googleLoginClientId(
          (r) => {
            this.clientID = r.client_id;
          },
          () => {
            // stub
          },
        );
      },
      async handleLogin() {
        this.error = null;
        const { loginEmail: email, loginPassword: password } = this;

        const onSuccess = () => {
          this.goToCabinet();
        };

        const onError = (data, code) => {
          if (code === 500) {
            this.error = 'Internal Server Error';
          } else if (code === 502) {
            this.error = 'Bad gateway';
          } else if (
            code === 401 &&
            data.detail === 'No active account found with the given credentials'
          ) {
            this.error = 'Wrong credentials';
          } else {
            this.error = data?.detail || `Error code ${code.toString()}`;
          }
          this.isLoading = false;
        };

        try {
          this.isLoading = true;

          await AuthService.login(email, password, onSuccess, onError);
        } catch {
          this.isLoading = false;
        }
      },

      async signup() {
        this.error = null;
        this.validate = true;
        if (!this.emailValid || !this.passwordValid) {
          return;
        }

        const onSuccess = () => {
          const onError = (data, code) => {
            this.error = `Account created but can't log in, error: ${code.toString()}`;
          };
          AuthService.login(this.signupEmail, this.signupPassword, this.goToCabinet, onError);
        };

        const onError = (data, code) => {
          if (data.code === 'EXISTS') {
            this.$router.push({ name: 'login' });
            this.error = data.detail;
            this.loginEmail = this.signupEmail;
          } else {
            this.error = data?.detail || `Error code ${code.toString()}`;
          }
          this.isLoading = false;
        };

        // TODO: Move signup to AuthService?
        try {
          this.isLoading = true;

          await AuthAPI.signup(this.signupEmail, this.signupPassword, onSuccess, onError);
        } catch {
          this.isLoading = false;
        }
      },

      signInWithGoogle(googleToken) {
        const onSuccess = () => {
          this.goToCabinet();
        };

        const onError = (data, code) => {
          if (code === 500) {
            this.error = 'Internal Server Error';
          } else if (
            code === 401 &&
            data.detail === 'No active account found with the given credentials'
          ) {
            this.error = 'Wrong credentials';
          } else {
            this.error = data?.detail || `Error code ${code.toString()}`;
          }
        };

        AuthService.googleLogin(googleToken, onSuccess, onError);
      },

      goToCabinet() {
        window.location.href = this.$route.query?.referer
          ? decodeURIComponent(this.$route.query.referer)
          : '/app/devices/';
      },

      goToIndex() {
        window.location.href = '/';
      },
    },
  };
</script>

<style lang="scss">
  .recover-password-label {
    color: $color-text-third;
    text-align: right;
    margin-top: 10px;
    margin-bottom: 10px;
  }

  .terms {
    text-align: left;
    margin-bottom: 8px;
    color: $color-text-third;

    a {
      color: $color-text-third;
    }
  }
</style>
